import { JetView } from 'webix-jet'; // Importing JetView from webix-jet
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the filter of the Consultation Documents view.
 * @extends JetView
 */
export default class FilterConsultationDocumentsView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const canSearch = devConfig.getPermAction('consultationDocumentsSearch', 'Add'); // Consultar button permission
        const lang = this.app.getService('locale').getLang(); // Current language for the date range picker

        // Filter fields, shared by the accordion (large screens) and the popup (small screens)
        const fields = (suffix) => ({
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    responsive: 'consultationDocumentsFilterLayout' + suffix, // Responsive layout ID
                    cols: [
                        {
                            view: 'richselect', // Dropdown for Type, options from TypeDocument
                            name: 'Type',
                            id: 'consultationDocumentsType' + suffix,
                            minWidth: 200,
                            label: _('Tipo de documento'), // Label for Type
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                        },
                        {
                            view: 'text', // Text input for Name
                            name: 'Name',
                            minWidth: 200,
                            label: "<span class='icon-user'></span>" + _('Nombre'), // Label with icon
                            labelPosition: 'top',
                            placeholder: _('Nombre'), // Placeholder text
                        },
                        {
                            view: 'text', // Date range, sent as dateFrom/dateTo like the other reports
                            name: 'Datepicker',
                            css: 'reportrange2', // CSS class used by the date range picker
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de publicación'), // Label with icon
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            on: {
                                // Event handlers
                                onFocus: function () {
                                    devConfig.setDateModal(lang); // Set date modal on focus
                                },
                            },
                        },
                    ],
                },
            ],
        });

        const buttons = {
            margin: 10,
            cols: [
                {},
                {
                    view: 'button', // Button to clear form
                    value: _('Limpiar'),
                    width: 100,
                    click: () => {
                        $$('consultationDocumentsForm').setValues($$('consultationDocumentsForm').getCleanValues()); // Clear form values
                    },
                },
                {
                    view: 'button', // Button to consult documents
                    value: _('Consultar'),
                    hotkey: 'enter',
                    type: 'form',
                    width: 110,
                    hidden: !canSearch, // Conditional visibility
                    css: 'prm-consultationDocumentsSearch',
                    click: () => {
                        if (!canSearch) return; // Enter hotkey without permission
                        this.app.callEvent('consultationDocuments:filter', [this.getRoot().getValues()]); // Trigger filter event
                        $$('consultationDocumentsFilters').collapse(); // Collapse the filter section
                    },
                },
            ],
        };

        const buttons2 = {
            padding: 10,
            cols: [
                {
                    view: 'button', // Button to clear second form
                    value: _('Limpiar'),
                    click: () => {
                        $$('consultationDocumentsForm2').setValues($$('consultationDocumentsForm2').getCleanValues()); // Clear second form values
                    },
                },
                {
                    view: 'button', // Button to consult with second form
                    value: _('Consultar'),
                    type: 'form',
                    hidden: !canSearch, // Conditional visibility
                    css: 'prm-consultationDocumentsSearch',
                    click: () => {
                        this.app.callEvent('consultationDocuments:filter', [$$('consultationDocumentsForm2').getValues()]); // Trigger filter event
                        $$('consultationDocumentsFilterPopup').hide(); // Hide the filter popup
                    },
                },
            ],
        };

        const accordion_section = {
            type: 'wide',
            rows: [
                {
                    multi: true,
                    view: 'accordion', // Accordion for filters
                    css: 'betShop',
                    collapsed: true,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros'), // Header for filters
                            id: 'consultationDocumentsFilters',
                            body: {
                                padding: 10,
                                rows: [fields(''), buttons], // Filter fields and buttons
                            },
                        },
                    ],
                },
            ],
        };

        // Popup window for filters on small screens, destroyed with the view so it is not duplicated
        this.filterPopup = webix.ui({
            view: 'window',
            move: true,
            id: 'consultationDocumentsFilterPopup',
            fullscreen: true,
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Filtros'), align: 'center' }, // Label for popup
                    {
                        view: 'icon', // Close icon for popup
                        icon: 'wxi-close',
                        click: function () {
                            this.getTopParentView().hide(); // Hide popup on close
                        },
                    },
                ],
            },
            modal: true,
            position: 'center',
            body: {
                view: 'form',
                id: 'consultationDocumentsForm2',
                type: 'clean',
                rows: [
                    {
                        view: 'scrollview', // Scrollable view for filters
                        scroll: 'xy',
                        body: {
                            padding: 10,
                            rows: [fields('2')], // Filter fields
                        },
                    },
                    buttons2,
                ],
            },
        });

        return {
            view: 'form',
            id: 'consultationDocumentsForm', // Main form ID
            type: 'clean',
            rows: [
                screen.width > 500 && screen.height > 500
                    ? accordion_section // Show accordion if screen size is sufficient
                    : { height: 1, width: 0 },
            ],
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     */
    init() {
        const _ = this.app.getService('locale')._; // Localization service
        var typeRequested = false; // TypeDocument is requested once and its options reused afterwards
        var loadTypes = function () {
            if (typeRequested) {
                return;
            }
            typeRequested = true;
            webix
                .ajax()
                .headers({
                    'Content-type': 'application/json', // Set content type for AJAX request
                })
                .post(devConfig.geturlApi() + 'RegisteredDocument/TypeDocument', '', {
                    error: function () {
                        typeRequested = false; // Allow a retry on the next click
                        webix.message(_('Error! ')); // Show error message
                    },
                    success: function (text) {
                        var resp = JSON.parse(text); // Parse response
                        ['consultationDocumentsType', 'consultationDocumentsType2'].forEach(function (id) {
                            if ($$(id)) {
                                $$(id).define('options', resp.Data); // Update options for Type dropdown
                                $$(id).refresh(); // Refresh dropdown
                            }
                        });
                    },
                });
        };
        webix.ready(function () {
            // Options are loaded on the first click of either Type dropdown
            ['consultationDocumentsType', 'consultationDocumentsType2'].forEach(function (id) {
                if ($$(id)) {
                    $$(id).attachEvent('onItemClick', loadTypes);
                }
            });
        });
    }

    /**
     * Destroys the filter popup together with the view.
     */
    destroy() {
        if (this.filterPopup) {
            this.filterPopup.destructor(); // Remove the popup window
        }
    }
}

// FILE DOCUMENTED
